/**
 * feedbackCategories.ts
 *
 * Client-side mirror of the server-side FEEDBACK_CATEGORIES and related types
 * from src/models/EditPlugin/systemPlugins.ts.
 *
 * Keep this in sync with the server definition whenever categories change.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FeedbackCategory {
  key: string;
  name: string;
  icon: string;
  description: string;
  dropdownDescription: string;
}

/**
 * Minimal plugin shape needed for grouping by category.
 * Mirrors EditPluginWithInstallation from the server, with only the fields
 * used on the frontend for category-aware display.
 */
export interface FeedbackPlugin {
  id: string;
  name: string;
  category: string | null;
  icon: string | null;
  defaultOrder: number | null;
  enabled: boolean;
  installed: boolean;
  type: "SYSTEM" | "USER";
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// FEEDBACK_CATEGORIES — the 4 top-level categories
// (mirrored from src/models/EditPlugin/systemPlugins.ts)
// ---------------------------------------------------------------------------

export const FEEDBACK_CATEGORIES: FeedbackCategory[] = [
  {
    key: "developmental",
    name: "Developmental",
    icon: "feedback-developmental",
    description: "Big-picture story structure, consistency, pacing, and clarity",
    dropdownDescription: "Strengthen story logic, pacing, and impact",
  },
  {
    key: "line",
    name: "Line",
    icon: "feedback-line",
    description: "Sentence-level prose quality, word choice, and rhythm",
    dropdownDescription: "Sharper sentences, smoother flow",
  },
  {
    key: "dialogue",
    name: "Dialogue",
    icon: "feedback-dialogue",
    description: "How characters speak: attribution, voice, and clarity",
    dropdownDescription: "Make dialogue feel natural",
  },
  {
    key: "copy",
    name: "Copy",
    icon: "feedback-copy",
    description: "Grammar, punctuation, and mechanical correctness",
    dropdownDescription: "Grammar, punctuation, and mechanics—fixed",
  },
  {
    key: "beta_read",
    name: "Beta Read",
    icon: "feedback-beta-read",
    description: "Different perspectives on your story",
    dropdownDescription: "Different perspectives on your story",
  },
];

/**
 * Set of valid category keys for O(1) lookup.
 */
export const FEEDBACK_CATEGORY_KEYS: Set<string> = new Set(
  FEEDBACK_CATEGORIES.map((c) => c.key)
);

/**
 * Maps lowercase built-in feedback type names to their category key.
 * Mirrors the category assignments in src/models/EditPlugin/systemPlugins.ts.
 * Used to infer the correct icon/category for agent-created comments that
 * carry a feedbackType but no feedbackCategory.
 */
export const SYSTEM_FEEDBACK_TYPE_CATEGORY_MAP: Record<string, string> = {
  "consistency": "developmental",
  "believability": "developmental",
  "pacing": "developmental",
  "clarity": "developmental",
  "originality": "developmental",
  "line edits": "line",
  "echoes": "line",
  "glue words": "line",
  "crutch words": "line",
  "dialogue tags": "dialogue",
  "dialogue clarity": "dialogue",
  "grammar": "copy",
  "passive voice": "copy",
  "punctuation": "copy",
  "maya": "beta_read",
  "anton": "beta_read",
  "joan": "beta_read",
};

/**
 * Map from category key → FeedbackCategory for quick lookup.
 */
export const FEEDBACK_CATEGORY_MAP: Map<string, FeedbackCategory> = new Map(
  FEEDBACK_CATEGORIES.map((c) => [c.key, c])
);

/**
 * Map from lowercased display name → category key.
 * Mirrors what the server stores when the LLM emits the `category` field
 * using the human-readable name (e.g. "Beta Read") rather than the
 * underscore-key. Required to render correct labels for any category whose
 * display name does not match its key when lowercased (e.g. "Beta Read" vs
 * "beta_read"). For single-word categories (Developmental, Line, Dialogue,
 * Copy) the lowercased name already equals the key so this map is a no-op.
 */
export const FEEDBACK_CATEGORY_NAME_TO_KEY: Record<string, string> =
  FEEDBACK_CATEGORIES.reduce<Record<string, string>>((acc, c) => {
    acc[c.name.toLowerCase()] = c.key;
    return acc;
  }, {});

/**
 * Resolve any category input (key, display name, or noisy LLM output) to the
 * canonical category key, or null if unrecognized.
 */
export function resolveCategoryKey(
  input: string | null | undefined
): string | null {
  if (!input || typeof input !== "string") return null;
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  if (FEEDBACK_CATEGORY_KEYS.has(normalized)) return normalized;
  if (FEEDBACK_CATEGORY_NAME_TO_KEY[normalized] !== undefined) {
    return FEEDBACK_CATEGORY_NAME_TO_KEY[normalized];
  }
  if (normalized === "custom") return "custom";
  return null;
}

// ---------------------------------------------------------------------------
// Icon path helpers
// ---------------------------------------------------------------------------

const CATEGORY_ICON_PATH_MAP: Record<string, string> = {
  "feedback-developmental": "/assets/feedback-developmental.svg",
  "feedback-line": "/assets/feedback-line.svg",
  "feedback-dialogue": "/assets/feedback-dialogue.svg",
  "feedback-copy": "/assets/feedback-copy.svg",
  "feedback-beta-read": "/assets/feedback-beta-read.svg",
};

const DEFAULT_ICON_PATH = "/assets/feedback-developmental.svg";
const FALLBACK_ICON_PATH = "/assets/edit.svg";

/**
 * Returns the SVG asset path for a given category key or icon key.
 *
 * Accepts either a category key ("developmental") or an icon key
 * ("feedback-developmental"). For null/undefined, returns the default
 * Developmental icon. For unknown keys, returns the generic plugin icon.
 */
export function getCategoryIconPath(
  keyOrIconKey: string | null | undefined
): string {
  if (!keyOrIconKey) return DEFAULT_ICON_PATH;

  const normalized = keyOrIconKey.toLowerCase();

  // Try as a category key first (e.g., "developmental" or "Developmental")
  const category = FEEDBACK_CATEGORY_MAP.get(normalized);
  if (category) {
    return CATEGORY_ICON_PATH_MAP[category.icon] || FALLBACK_ICON_PATH;
  }

  // Try as an icon key directly (e.g., "feedback-developmental")
  return CATEGORY_ICON_PATH_MAP[normalized] || FALLBACK_ICON_PATH;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Minimal plugin shape from useEditPluginInstallations (name match for feedback type). */
export type FeedbackInstallationLike = { installed?: boolean; name?: string };

/**
 * Aligns AI comment header logic across FeedbackCard and Comment (share link / inline):
 * built-in system types are always known; otherwise the type must match an installed plugin name.
 */
export function resolveFeedbackTypeRecognition(
  feedbackType: string | null | undefined,
  installations: FeedbackInstallationLike[] | null | undefined
): { inferredCategoryFromType: string | null; isKnownFeedbackType: boolean } {
  const normalizedType = feedbackType?.trim().toLowerCase() ?? "";
  const inferredCategoryFromType = normalizedType
    ? SYSTEM_FEEDBACK_TYPE_CATEGORY_MAP[normalizedType] ?? null
    : null;
  const isKnownFeedbackType =
    inferredCategoryFromType != null ||
    (!normalizedType
      ? true
      : (installations ?? []).some((plugin) => {
          if (!plugin?.installed) return false;
          const pluginName = typeof plugin.name === "string" ? plugin.name.trim().toLowerCase() : "";
          return pluginName === normalizedType;
        }));
  return { inferredCategoryFromType, isKnownFeedbackType };
}

/**
 * Returns a formatted display label for a feedback category.
 * Used in comment headers: "DEVELOPMENTAL"
 *
 * For built-in categories, returns "[CATEGORY]".
 * For explicit custom category values, falls back to feedbackType
 * if provided, returning "[TYPE NAME]".
 * For unknown/untyped labels, uses "COMMENT" variants so we don't
 * imply a recognized feedback type.
 */
export function getFeedbackCategoryLabel(
  category: string | null | undefined,
  feedbackType?: string | null | undefined,
  opts?: { isKnownFeedbackType?: boolean }
): string {
  const typeName = feedbackType?.trim();
  const normalizedType = typeName?.toLowerCase();
  // Infer category from built-in type name when not explicitly set.
  const inferredCategory = (!category && normalizedType)
    ? SYSTEM_FEEDBACK_TYPE_CATEGORY_MAP[normalizedType] ?? null
    : null;
  // Normalize the explicit category to its canonical key. The LLM may emit
  // the display name ("Beta Read") rather than the key ("beta_read"); without
  // this normalization, multi-word category names fail every downstream
  // FEEDBACK_CATEGORY_KEYS check and the card falls back to SUDOWRITE.
  const normalizedExplicitKey = resolveCategoryKey(category);
  const explicitCategoryKey = normalizedExplicitKey;
  const key = explicitCategoryKey ?? inferredCategory ?? null;
  const hasExplicitSystemFeedbackCategory =
    explicitCategoryKey != null &&
    (FEEDBACK_CATEGORY_KEYS.has(explicitCategoryKey) || explicitCategoryKey === "custom");
  const isKnownFeedbackType =
    opts?.isKnownFeedbackType ?? (inferredCategory != null || hasExplicitSystemFeedbackCategory);

  if (typeName) {
    if (!isKnownFeedbackType) {
      return `COMMENT - ${typeName.toUpperCase()}`;
    }
    if ((key && FEEDBACK_CATEGORY_KEYS.has(key)) || key === "custom") {
      return typeName.toUpperCase();
    }
    return `COMMENT - ${typeName.toUpperCase()}`;
  }
  if (key && FEEDBACK_CATEGORY_KEYS.has(key)) {
    const cat = FEEDBACK_CATEGORY_MAP.get(key)!;
    return cat.name.toUpperCase();
  }
  if (key === "custom") {
    return "FEEDBACK";
  }
  return "COMMENT";
}

/**
 * Resolves the display header label and icon for an AI feedback comment.
 * Shared between FeedbackCard and Comment (share link / inline) to avoid duplication.
 */
export function resolveFeedbackHeader(
  metadata: { feedbackCategory?: string | null; feedbackType?: string | null } | null | undefined,
  installations: FeedbackInstallationLike[] | null | undefined
): { headerLabel: string; headerIcon: string } {
  const categoryKey = (metadata?.feedbackCategory as string) ?? null;
  const feedbackType = (metadata?.feedbackType as string) ?? null;
  const hasStructuredFeedbackMetadata =
    (typeof categoryKey === "string" && categoryKey.trim().length > 0) ||
    (typeof feedbackType === "string" && feedbackType.trim().length > 0);
  const { inferredCategoryFromType, isKnownFeedbackType } = resolveFeedbackTypeRecognition(
    feedbackType,
    installations
  );
  // Normalize the stored category to its canonical key. The LLM may emit the
  // display name ("Beta Read") rather than the key ("beta_read"); without
  // normalization, multi-word category names fail downstream key checks and
  // the card falls back to SUDOWRITE.
  const normalizedExplicitKey = resolveCategoryKey(categoryKey);
  const effectiveCategoryKey = normalizedExplicitKey || inferredCategoryFromType;
  const categoryLabel = getFeedbackCategoryLabel(effectiveCategoryKey, feedbackType, { isKnownFeedbackType });
  const isValidFeedbackType =
    isKnownFeedbackType &&
    categoryLabel !== "COMMENT" &&
    !categoryLabel.startsWith("COMMENT -");
  return {
    headerLabel: hasStructuredFeedbackMetadata && isValidFeedbackType ? categoryLabel : "SUDOWRITE",
    headerIcon: hasStructuredFeedbackMetadata && isValidFeedbackType
      ? getCategoryIconPath(effectiveCategoryKey)
      : "/assets/comment-icon.svg",
  };
}

/**
 * Groups an array of plugins by their category key.
 *
 * Plugins without a category (user plugins) are placed in the "custom" bucket.
 * Returns an object with a key for each of the 5 system categories plus "custom".
 *
 * System-category buckets are sorted by `defaultOrder` (deterministic, matches
 * the canonical order in `SYSTEM_EDIT_PLUGINS`). The dropdown doesn't expose
 * intra-category drag-reorder, so trusting the user's `edit_plugin_order` here
 * surfaces whatever cuid order `installMissingSystemPlugins` happened to insert
 * them in (e.g. "Maya / Joan / Anton" when Anton/Three
 * were seeded out-of-order). The custom bucket preserves input order since
 * users CAN reorder custom plugins.
 */
export function getPluginsByCategory(
  plugins: FeedbackPlugin[]
): Record<string, FeedbackPlugin[]> {
  const result: Record<string, FeedbackPlugin[]> = {
    developmental: [],
    line: [],
    dialogue: [],
    copy: [],
    beta_read: [],
    custom: [],
  };

  for (const plugin of plugins) {
    if (plugin.category && FEEDBACK_CATEGORY_KEYS.has(plugin.category)) {
      result[plugin.category].push(plugin);
    } else if (plugin.type === "USER") {
      // Only user-created plugins go in the custom bucket.
      // Old system plugins without a category are excluded.
      result.custom.push(plugin);
    }
  }

  // Sort each system-category bucket by defaultOrder ascending. Plugins with
  // null/undefined defaultOrder sort to the end. Custom bucket preserved.
  for (const key of FEEDBACK_CATEGORY_KEYS) {
    result[key].sort((a, b) => {
      const ao = a.defaultOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.defaultOrder ?? Number.MAX_SAFE_INTEGER;
      return ao - bo;
    });
  }

  return result;
}
